import React from 'react';
import { Text } from '@charlietango/ui';
import { rem } from '../../styles/rem';

import { colors } from '../../styles/colors';
import { TextVariant } from '../../styles/typography';
import TextBlockViewModel from '../../view-models/TextBlockViewModel';
import RichText from '../RichText/RichText';

export enum AccordionOrderTypes {
  number = 'number',
  bullet = 'bullet',
  none = 'none',
}

export type AccordionProps = {
  items?: TextBlockViewModel[];
  orderType?: AccordionOrderTypes;
  indicator?: boolean;
  background?: string | null;
  interpolationParams?: Record<string, React.ReactNode>;
};

type AccordionItemProps = {
  item: TextBlockViewModel;
  orderType: AccordionOrderTypes;
  index: number;
  indicator: boolean;
  interpolationParams?: Record<string, React.ReactNode>;
};

function AccordionArrow() {
  return (
    <span
      aria-hidden="true"
      css={{
        width: rem(16),
        height: rem(16),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transform: 'rotate(0deg)',
      }}
    >
      <svg focusable={false} width="16" height="16" viewBox="0 0 20 20">
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M10.1425 10.407C10.0636 10.4773 9.94497 10.4773 9.86601 10.4071C9.27976 9.88606 6.8923 7.76172 3.89525 5.06493C3.43575 4.65148 2.81722 4.40683 2.26535 4.68527C2.02101 4.80856 1.74896 4.9896 1.47693 5.2556C1.16028 5.56518 0.961287 5.87477 0.836229 6.1416C0.611821 6.62039 0.788754 7.15385 1.11466 7.57027C3.3861 10.4725 6.64163 13.3381 8.51767 14.8941C9.38509 15.6135 10.6238 15.6136 11.4912 14.8943C13.3677 13.3382 16.6231 10.4721 18.8874 7.56931C19.2117 7.15364 19.3875 6.62172 19.1643 6.14414C19.0393 5.87672 18.84 5.56618 18.5224 5.25564C18.2505 4.98981 17.9787 4.80877 17.7345 4.68543C17.1825 4.40668 16.5636 4.65131 16.1039 5.06506C13.1083 7.76139 10.7274 9.88556 10.1425 10.407Z"
          fill="#165050"
        />
      </svg>
    </span>
  );
}

function AccordionItem({
  item,
  orderType,
  indicator,
  interpolationParams,
  index,
}: AccordionItemProps) {
  const isFirst = index === 0;

  return (
    <details
      css={{
        interpolateSize: 'allow-keywords',
        boxShadow: isFirst
          ? 'none'
          : `0 1px 0 0 ${colors.components.accordion.ListItem.Background}, inset 0 1px 0 0 ${colors.components.accordion.ListItem.Background}`,
        '&[open] > summary [data-accordion-arrow]': {
          transform: 'rotate(180deg)',
        },
        '@supports (transition-behavior: allow-discrete)': {
          '& [data-accordion-arrow]': {
            transition: 'transform 250ms ease',
          },
          '&::details-content': {
            blockSize: 0,
            overflow: 'hidden',
            opacity: 0,
            transition:
              'block-size 250ms ease, opacity 200ms ease, content-visibility 250ms',
            transitionBehavior: 'allow-discrete',
          },
          '&[open]::details-content': {
            blockSize: 'auto',
            opacity: 1,
          },
          '@media (prefers-reduced-motion: reduce)': {
            '& [data-accordion-arrow]': {
              transitionDuration: '0ms',
            },
            '&::details-content': {
              transitionDuration: '0ms',
            },
          },
        },
      }}
    >
      <summary
        sx={{
          py: [rem(20), rem(20), rem(16)],
          pl:
            orderType === AccordionOrderTypes.none && !indicator
              ? 0
              : [rem(16), rem(16), rem(40)],
          pr: [rem(16), rem(16), rem(40)],
          userSelect: 'none',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          listStyle: 'none',
          '::-webkit-details-marker': {
            display: 'none',
          },
          '::marker': {
            content: '""',
          },
        }}
      >
        <Text
          variant={TextVariant.BodyLg}
          as="h3"
          sx={{
            flexGrow: 2,
            maxWidth: '100%',
            margin: 0,
            lineHeight: 1.3,
            letterSpacing: 0,
            color: colors.base.Text.Primary,
            textWrap: 'pretty',
          }}
        >
          {item.title}
        </Text>
        <span data-accordion-arrow>
          <AccordionArrow />
        </span>
      </summary>
      <div
        sx={{
          pl: orderType === AccordionOrderTypes.none ? 0 : [0, 0, 5],
          pr: [0, 0, 5],
          backgroundColor: colors.components.accordion.ListItem.Background,
        }}
      >
        <RichText
          sx={{
            color: colors.base.Text.Brand,
            padding: [`${rem(24)} ${rem(20)}`, `${rem(80)} ${rem(80)}`],
            maxWidth:
              orderType === AccordionOrderTypes.none ? rem(490) : '100%',
            '> h4': {
              mt: [rem(28), rem(36)],
              mb: rem(8),
            },
            '> p, > ul, > ol': {
              mt: 0,
              mb: [rem(28), rem(36)],
            },
            '> ul, > ol': {
              pl: rem(32),
            },
            '> li, > ul li, > ol li': {
              my: 0,
            },
          }}
          headingsOffset={2}
          minHeadingLevel={4}
          maxHeadingLevel={4}
          variant={TextVariant.BodyLg}
          params={interpolationParams}
        >
          {item.text}
        </RichText>
      </div>
    </details>
  );
}

function Accordion({
  items,
  orderType = AccordionOrderTypes.number,
  indicator = true,
  background = colors.base.Background.White,
  interpolationParams,
}: AccordionProps) {
  return items && items.length > 0 ? (
    <div
      sx={{
        backgroundColor: background
          ? colors.components.accordion.Background.Default
          : 'none',
        padding: '24px 0',
        borderRadius: 16,
      }}
    >
      {items.map((item, index) => (
        <AccordionItem
          item={item}
          orderType={orderType}
          index={index}
          key={index}
          indicator={indicator}
          interpolationParams={interpolationParams}
        />
      ))}
    </div>
  ) : null;
}

export default Accordion;
